import { Component, ErrorHandler, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'

import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { MatDialog } from '@angular/material/dialog'
import { MatSelectChange } from '@angular/material/select'

import { BehaviorSubject, Observable, combineLatest, firstValueFrom, of as observableOf } from 'rxjs'
import { map, switchMap, take, tap } from 'rxjs/operators'

import { TuettuKieli } from '@lemon/core/model/common'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { Kirjanpitotili } from 'app/_jaettu-lemonator/model/kirjanpito'
import { HierarkiaKirjanpitotili } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'
import { KirjanpitotiliMuokkaaDialog, KirjanpitotiliMuokkaaDialogData } from './kirjanpitotili-muokkaa.dialog'

/** Flat node with expandable and level information */
interface FlatKirjanpitotili {
  expandable: boolean
  name: string
  level: number
  kirjanpitotili: Kirjanpitotili
}

@Component({
  templateUrl: './paatilikartta.component.html',
  styleUrls: ['./paatilikartta.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaatilikarttaComponent {
  readonly loading = signal(true)
  readonly commonError = signal('')
  flatatyt: Observable<FlatKirjanpitotili[]>

  valittuKieliSubject: BehaviorSubject<TuettuKieli> = new BehaviorSubject('fi')
  kieliValinta = form(signal<TuettuKieli>(this.valittuKieliSubject.value))

  // Debug thingies
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  tilikarttaUriObservable: Observable<string>
  tilikarttaEncodedUriObservable: Observable<string>

  // Permission for editing päätilikartta
  voidaanMuokataPaatilikarttaaObservable: Observable<boolean>

  constructor(
    private errorHandler: ErrorHandler,
    private dialog: MatDialog,
    private tilikarttaService: TilikarttaService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
  ) {
    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable

    // Permission to edit päätilikartta: IT + KAYTTOTUKI + MUUT teams, or Mia Hassi
    this.voidaanMuokataPaatilikarttaaObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      switchMap(kirjanpitaja => {
        if (!kirjanpitaja?.uid) {
          return observableOf(false)
        }
        if (
          kirjanpitaja.uid === 'XwQbL3nHV7TY8gyFKBsODwSsL0X2' || // Mia Hassi
          kirjanpitaja.uid === 'cVYOWEOwXxcTRBAxdChEK6IPM3u2' // Tarja Nieminen
        ) {
          return observableOf(true)
        }
        return this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiTaiMuutObservable
      }),
    )

    this.tilikarttaUriObservable = this.kirjanpitajaOnDevaajaObservable.pipe(
      map(devaaja => {
        return 'admin/paatilikartta'
      }),
    )
    this.tilikarttaEncodedUriObservable = this.tilikarttaUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )

    this.flatatyt = combineLatest([this.tilikarttaService.paatilikartanTilihierarkiaObservable, this.valittuKieliSubject]).pipe(
      map(([hierarkia, kieli]) => {
        const kaikki: FlatKirjanpitotili[] = []
        for (const juuri of hierarkia) {
          this.flattaa(juuri, 1, kaikki, kieli)
        }
        return kaikki
      }),
      tap(() => this.loading.set(false)),
    )
  }

  kieliMuuttui(change: MatSelectChange) {
    this.valittuKieliSubject.next(change.value)
  }

  private flattaa(hierarkia: HierarkiaKirjanpitotili, level: number, kaikki: FlatKirjanpitotili[], kieli: TuettuKieli) {
    kaikki.push(this.makeFlat(hierarkia, level, kieli))
    if (hierarkia.lapset) {
      for (const lapsi of hierarkia.lapset) {
        this.flattaa(lapsi, level + 1, kaikki, kieli)
      }
    }
  }

  private makeFlat(node: HierarkiaKirjanpitotili, level: number, kieli: TuettuKieli): FlatKirjanpitotili {
    const nimi = this._annaNimi(node, kieli)
    return {
      expandable: !!node.lapset && node.lapset.length > 0,
      name: node.kirjanpitotili.numero + ' ' + nimi,
      level: level,
      kirjanpitotili: node.kirjanpitotili,
    }
  }

  private _annaNimi(node: HierarkiaKirjanpitotili, kieli: TuettuKieli): string {
    if (node.kirjanpitotili.localisedName) {
      if (node.kirjanpitotili.localisedName[kieli]) {
        return node.kirjanpitotili.localisedName[kieli]
      }
    }
    return node.kirjanpitotili.nimi
  }

  async muokkaa(tili: Kirjanpitotili) {
    const voidaan = await firstValueFrom(this.voidaanMuokataPaatilikarttaaObservable)
    if (!voidaan) {
      return
    }
    const data: KirjanpitotiliMuokkaaDialogData = {
      tili: tili,
      lisaa: false,
    }
    this.dialog.open(KirjanpitotiliMuokkaaDialog, { data: data, autoFocus: false })
  }

  async lisaa() {
    const voidaan = await firstValueFrom(this.voidaanMuokataPaatilikarttaaObservable)
    if (!voidaan) {
      return
    }
    const data: KirjanpitotiliMuokkaaDialogData = {
      tili: {
        vanhempi: null,
        aktiivinen: true,
        nimi: null,
        numero: null,
        oletusAlvKasittely: null,
        alvTyyppi: null,
        localisedName: {} as any,
      },
      lisaa: true,
    }
    this.dialog.open(KirjanpitotiliMuokkaaDialog, { data: data, autoFocus: false })
  }

  async drop(event: CdkDragDrop<FlatKirjanpitotili>) {
    const voidaan = await firstValueFrom(this.voidaanMuokataPaatilikarttaaObservable)
    if (!voidaan) {
      return
    }

    if (event.currentIndex === event.previousIndex) {
      return
    }
    combineLatest([this.flatatyt, this.tilikarttaService.paatilikartanTilihierarkiaObservable, this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable])
      .pipe(take(1))
      .subscribe(([flatatyt, hierarkia, kirjanpitaja]) => {
        const raahattu = flatatyt[event.previousIndex]
        const uusiIsanta = flatatyt[event.currentIndex]

        const raahattuHierarkiassa = this.tilikarttaService.etsiTiliHierarkiasta(raahattu.kirjanpitotili.numero, hierarkia)
        const uusiIsantaHierarkiassa = this.tilikarttaService.etsiTiliHierarkiasta(uusiIsanta.kirjanpitotili.numero, [raahattuHierarkiassa])

        if (uusiIsantaHierarkiassa) {
          return
        }

        const uusiVanhempi = event.currentIndex - event.previousIndex > 0 ? uusiIsanta.kirjanpitotili.numero : uusiIsanta.kirjanpitotili.vanhempi

        raahattu.kirjanpitotili.vanhempi = uusiVanhempi || null
        this.tilikarttaService.tallennaPaatilikartanTili(raahattu.kirjanpitotili, kirjanpitaja.uid).catch(error => {
          this.commonError.set('Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.')
          this.errorHandler.handleError(error)
        })
      })
  }
}
